<template>
  <div class="min-h-screen bg-gray-100">
    <van-nav-bar
      title="设备详情"
      left-arrow
      @click-left="$router.back()"
    />

    <!-- 设备基本信息 -->
    <div class="bg-white p-4 mb-4">
      <div class="flex items-center">
        <img :src="device.avatar" alt="设备头像" class="w-16 h-16 rounded-full">
        <div class="ml-4">
          <h2 class="text-lg font-medium">{{ device.name }}</h2>
          <p class="text-sm text-gray-500">{{ device.type }}</p>
          <p class="text-sm text-gray-500">设备ID：{{ device.id }}</p>
        </div>
      </div>
    </div>

    <!-- 设备状态 -->
    <div class="bg-white mb-4">
      <van-cell-group>
        <van-cell title="设备状态">
          <template #right-icon>
            <van-tag :type="device.status === 'online' ? 'success' : 'default'" round>
              {{ device.status === 'online' ? '在线' : '离线' }}
            </van-tag>
          </template>
        </van-cell>
        <van-cell title="电池电量" :value="`${device.battery}%`">
          <template #right-icon>
            <van-progress
              :percentage="device.battery"
              :color="getBatteryColor(device.battery)"
              :show-pivot="false"
              stroke-width="6"
            />
          </template>
        </van-cell>
        <van-cell title="信号强度" :value="`${device.signal}%`">
          <template #right-icon>
            <van-progress
              :percentage="device.signal"
              color="#38B2AC"
              :show-pivot="false"
              stroke-width="6"
            />
          </template>
        </van-cell>
        <van-cell title="固件版本" :value="device.firmwareVersion" />
        <van-cell title="最后更新" :value="formatDate(device.lastUpdate)" />
      </van-cell-group>
    </div>

    <!-- 设备设置 -->
    <div class="bg-white mb-4">
      <van-cell-group>
        <van-cell title="设备名称" is-link @click="showNameDialog = true" />
        <van-cell title="数据上报频率">
          <template #right-icon>
            <van-stepper v-model="reportInterval" min="1" max="60" />
          </template>
        </van-cell>
        <van-cell title="省电模式">
          <template #right-icon>
            <van-switch v-model="powerSaving" size="24" />
          </template>
        </van-cell>
        <van-cell title="震动提醒">
          <template #right-icon>
            <van-switch v-model="vibrationAlert" size="24" />
          </template>
        </van-cell>
        <van-cell title="LED指示灯">
          <template #right-icon>
            <van-switch v-model="ledIndicator" size="24" />
          </template>
        </van-cell>
      </van-cell-group>
    </div>

    <!-- 存储空间 -->
    <div class="bg-white mb-4">
      <div class="p-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm">存储空间</span>
          <span class="text-sm text-gray-500">
            {{ formatStorage(device.storageUsed) }} / {{ formatStorage(device.storageTotal) }}
          </span>
        </div>
        <van-progress
          :percentage="(device.storageUsed / device.storageTotal) * 100"
          :color="getStorageColor(device.storageUsed / device.storageTotal)"
          :show-pivot="false"
          stroke-width="8"
        />
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="p-4 space-y-4">
      <van-button 
        block 
        type="primary" 
        @click="updateFirmware"
        :loading="updating"
      >
        检查固件更新
      </van-button>
      <van-button 
        block 
        @click="resetDevice"
      >
        重置设备
      </van-button>
      <van-button 
        block 
        type="danger"
        @click="showUnbindDialog = true"
      >
        解绑设备
      </van-button>
    </div>

    <!-- 修改名称弹窗 -->
    <van-dialog
      v-model="showNameDialog"
      title="修改设备名称"
      show-cancel-button
      @confirm="updateDeviceName"
    >
      <div class="p-4">
        <van-field
          v-model="newDeviceName"
          placeholder="请输入新的设备名称"
          :rules="[{ required: true, message: '请输入设备名称' }]"
        />
      </div>
    </van-dialog>

    <!-- 解绑确认弹窗 -->
    <van-dialog
      v-model="showUnbindDialog"
      title="解绑设备"
      show-cancel-button
      @confirm="unbindDevice"
    >
      <p class="p-4">确定要解绑该设备吗？解绑后需要重新添加才能使用。</p>
    </van-dialog>
  </div>
</template>

<script>
import { formatDate } from '@/utils'

export default {
  name: 'DeviceInfo',
  data() {
    return {
      device: {
        id: '',
        name: '',
        type: '',
        avatar: '',
        status: 'offline',
        battery: 0,
        signal: 0,
        firmwareVersion: '',
        lastUpdate: null,
        storageUsed: 0,
        storageTotal: 1024 * 1024 * 1024 // 1GB
      },
      reportInterval: 5,
      powerSaving: false,
      vibrationAlert: true,
      ledIndicator: true,
      showNameDialog: false,
      showUnbindDialog: false,
      newDeviceName: '',
      updating: false
    }
  },
  methods: {
    formatDate,
    getBatteryColor(percentage) {
      if (percentage <= 20) return '#ee0a24'
      if (percentage <= 50) return '#ff976a'
      return '#07c160'
    },
    getStorageColor(ratio) {
      if (ratio >= 0.9) return '#ee0a24'
      if (ratio >= 0.7) return '#ff976a'
      return '#07c160'
    },
    formatStorage(bytes) {
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      
      return `${size.toFixed(1)} ${units[unitIndex]}`
    },
    async fetchDeviceInfo() {
      console.log('fetchDeviceInfo 方法被调用')
      
      // 使用模拟数据
      this.device = {
        id: '1',
        name: '小白',
        type: '智能项圈 Pro',
        avatar: 'https://img01.yzcdn.cn/vant/cat.jpeg',
        status: 'online',
        battery: 85,
        signal: 92,
        firmwareVersion: 'v2.1.4',
        lastUpdate: Date.now(),
        storageUsed: 256 * 1024 * 1024,
        storageTotal: 1024 * 1024 * 1024,
        reportInterval: 5,
        powerSaving: false,
        vibrationAlert: true,
        ledIndicator: true
      }
      
      // 初始化设置值
      this.reportInterval = this.device.reportInterval
      this.powerSaving = this.device.powerSaving
      this.vibrationAlert = this.device.vibrationAlert
      this.ledIndicator = this.device.ledIndicator
    },
    async updateDeviceName() {
      console.log('updateDeviceName 方法被调用')
      
      if (!this.newDeviceName) return
      
      this.device.name = this.newDeviceName
      this.newDeviceName = ''
      this.$toast.success('设备名称已更新')
    },
    async updateFirmware() {
      console.log('updateFirmware 方法被调用')
      
      this.updating = true
      setTimeout(() => {
        this.$toast('当前已是最新版本')
        this.updating = false
      }, 1000)
    },
    async resetDevice() {
      console.log('resetDevice 方法被调用')
      
      this.$toast.success('设备已重置')
    },
    async unbindDevice() {
      console.log('unbindDevice 方法被调用')
      
      this.$toast.success('设备已解绑')
      this.$router.replace('/device-list')
    }
  },
  watch: {
    reportInterval(val) {
      console.log('reportInterval 变更为:', val)
    },
    powerSaving(val) {
      console.log('powerSaving 变更为:', val)
    },
    vibrationAlert(val) {
      console.log('vibrationAlert 变更为:', val)
    },
    ledIndicator(val) {
      console.log('ledIndicator 变更为:', val)
    }
  },
  async created() {
    console.log('DeviceInfo 页面已创建')
    await this.fetchDeviceInfo()
  }
}
</script> 